import { Button, Drawer } from "@a2z/ui";
import { useI18n, useTranslate } from "@/lib/i18n";
import { billPlaceLabel } from "@/lib/invoice/place";
import type { KitchenBoardPastPlate, KitchenBoardPlate } from "@/lib/api/types";
import { isOffPremise } from "../lib/channel";
import { ChannelShape, ticketKeyOf, useMinutesLabel } from "./PaperKitchen";

/**
 * «المُنجز مؤخرًا» — THE RECALL DRAWER (T2, T3; D167, D170).
 *
 * On the board's header for a session that may cook or serve. Two lists:
 *  - plates a PERSON served in the last 15 minutes on OPEN bills (the
 *    payload's `recentServed`), one row per bill, each with «تراجع» →
 *    `undoServed` with the same plate ids (`ITEM_SERVE`);
 *  - READY plates that aged off the lanes on THIS device (T2's display rule),
 *    one row per ticket — or per ORDER where the order view aged a whole
 *    order off (`agedBy="order"`, gate P4 #1) — each with its hand-over
 *    («تقديم» / «تسليم», every plate of the row) and «إظهار», which pins the
 *    card back on this device only. Its clock is the row's LAST ready.
 * Surface-agnostic: the wall board and the floor's Kitchen tab both mount it.
 * The shared inset `Drawer`; nothing here is a new kind of control.
 */
type Group<T extends KitchenBoardPlate> = { key: string; first: T; plates: T[] };

function groupBy<T extends KitchenBoardPlate>(plates: readonly T[], keyOf: (plate: T) => string): Group<T>[] {
  const groups = new Map<string, Group<T>>();
  for (const plate of plates) {
    const key = keyOf(plate);
    const group = groups.get(key);
    if (group) group.plates.push(plate);
    else groups.set(key, { key, first: plate, plates: [plate] });
  }
  return [...groups.values()];
}

// eslint-disable-next-line no-restricted-syntax -- not chrome: a CONTENT panel composed ON the shared `Drawer` (variant="inset"), named for what it lists; it declares no frame of its own.
export function KitchenRecallDrawer({
  isOpen,
  onClose,
  recentServed,
  agedOff,
  now,
  canServe,
  busyItemIds,
  onUndoServed,
  onServe,
  onShow,
  agedBy = "ticket",
}: {
  /** How the aged-off rows group: ticket (lanes) or whole order (order view). */
  agedBy?: "ticket" | "order";
  isOpen: boolean;
  onClose: () => void;
  recentServed: readonly KitchenBoardPastPlate[];
  agedOff: readonly KitchenBoardPlate[];
  now: number;
  canServe: boolean;
  busyItemIds: ReadonlySet<string>;
  onUndoServed: (plates: KitchenBoardPlate[]) => void;
  onServe: (plates: KitchenBoardPlate[]) => void;
  onShow: (plateIds: string[]) => void;
}) {
  const t = useTranslate();
  const { locale } = useI18n();
  const minutesLabel = useMinutesLabel();
  const served = groupBy(recentServed, (plate) => plate.invoiceId);
  const aged = groupBy(agedOff, agedBy === "order" ? (plate) => plate.invoiceId : ticketKeyOf);
  const lastReadyAt = (plates: readonly KitchenBoardPlate[]) =>
    plates.reduce<string | null>(
      (last, plate) => (plate.readyAt && (!last || plate.readyAt > last) ? plate.readyAt : last),
      null,
    );
  const dishes = (plates: readonly KitchenBoardPlate[]) => {
    const counts = new Map<string, number>();
    for (const plate of plates) {
      const name = locale === "en" && plate.nameEn ? plate.nameEn : plate.nameAr;
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    return [...counts].map(([name, n]) => `${n}× ${name}`).join("  ·  ");
  };
  const minutesSince = (iso: string | null | undefined) => {
    if (!iso) return null;
    const at = new Date(iso).getTime();
    return Number.isNaN(at) ? null : Math.max(0, Math.floor((now - at) / 60_000));
  };
  const head = (first: KitchenBoardPlate, minutes: number | null) => (
    <span data-ui="kitchen-recall-head" className="flex flex-wrap items-center gap-x-2">
      <span data-ui="kitchen-ticket-number" className="tabular-nums">
        #{first.dailySequence}
      </span>
      <span data-ui="kitchen-ticket-place" className="flex items-center gap-1">
        <ChannelShape channel={first.channel} />
        {billPlaceLabel({ tableIds: first.tableNames, channel: first.channel }, (name) => name, t)}
      </span>
      <span className="flex-1" />
      <span data-ui="kitchen-ticket-time" className="tabular-nums">
        {minutesLabel.clock(minutes)}
      </span>
    </span>
  );
  const nothing = served.length === 0 && aged.length === 0;
  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      variant="inset"
      size="sm"
      title={t((m) => m.kitchen.recallOpen)}
      closeLabel={t((m) => m.common.close)}
      footerActions={
        <Button variant="outline" size="lg" data-testid="kitchen-recall-close" onClick={onClose}>
          {t((m) => m.common.close)}
        </Button>
      }
    >
      <div data-ui="kitchen-recall" data-testid="kitchen-recall" className="flex flex-col gap-5">
        {nothing ? <p data-ui="kitchen-recall-empty">{t((m) => m.kitchen.recallEmpty)}</p> : null}
        {aged.length > 0 ? (
          <section className="flex flex-col gap-2" data-testid="kitchen-recall-aged">
            <h3 data-ui="kitchen-recall-title">{t((m) => m.kitchen.recallAgedOffTitle)}</h3>
            <ul className="flex flex-col gap-2">
              {aged.map((group) => {
                const busy = group.plates.some((plate) => busyItemIds.has(plate.id));
                return (
                  <li
                    key={group.key}
                    data-ui="kitchen-recall-row"
                    data-testid={`kitchen-recall-aged-${group.key}`}
                    className="flex flex-col gap-2"
                  >
                    {head(group.first, minutesSince(lastReadyAt(group.plates)))}
                    <bdi data-ui="kitchen-recall-dishes">{dishes(group.plates)}</bdi>
                    <span className="flex flex-wrap gap-2">
                      {canServe ? (
                        <Button
                          variant="primary"
                          size="sm"
                          data-kind="serve"
                          disabled={busy}
                          data-testid={`kitchen-recall-serve-${group.key}`}
                          onClick={() => onServe(group.plates)}
                        >
                          {isOffPremise(group.first.channel)
                            ? t((m) => m.kitchen.actionHandOver)
                            : t((m) => m.kitchen.actionServed)}
                        </Button>
                      ) : null}
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`kitchen-recall-show-${group.key}`}
                        onClick={() => onShow(group.plates.map((plate) => plate.id))}
                      >
                        {t((m) => m.kitchen.show)}
                      </Button>
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}
        {served.length > 0 ? (
          <section className="flex flex-col gap-2" data-testid="kitchen-recall-served">
            <h3 data-ui="kitchen-recall-title">{t((m) => m.kitchen.recallServedTitle)}</h3>
            <ul className="flex flex-col gap-2">
              {served.map((group) => (
                <li
                  key={group.key}
                  data-ui="kitchen-recall-row"
                  data-testid={`kitchen-recall-served-${group.key}`}
                  className="flex flex-col gap-2"
                >
                  {head(group.first, minutesSince(group.first.servedAt))}
                  <bdi data-ui="kitchen-recall-dishes">{dishes(group.plates)}</bdi>
                  {canServe ? (
                    <span className="flex">
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`kitchen-recall-undo-${group.key}`}
                        onClick={() => onUndoServed(group.plates)}
                      >
                        {t((m) => m.kitchen.undo)}
                      </Button>
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </Drawer>
  );
}
